import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, selectUIStates, setUIStates } from "../../../redux/features/appData";
import { 
    Company,
    Address,
    Workspace,
    CheckCompany,
    CheckBusinessType,
    CheckIndustryType,
    CHECK_BUSINESS_TYPES,
    CHECK_INDUSTRY_TYPES,
    CheckFederalEINStatus,
    isCheckError,
    isValidPostalCode,
    isValidURL,
    isValidFederalEIN,
    formatFederalEIN,
    formatPostalCode
} from "winslow-core";
import {
  apiGetCompanyByWorkspaceId,
  apiUpdateCompany,
} from "../../../services/CompanyService";
import { 
  apiCheckUpdateFederalEIN,
  apiCheckGetCompany,
  apiCheckGetCompanyFederalEIN,
  apiCheckUpdateCompany,
} from "../../../services/PayrollService";
import {
  Input,
  Label,
  Button,
  Alert,
  AlertDescription,
  Tooltip,
  TooltipProvider,
} from "../../../ds2/components";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { Save, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import ModalBasicConfirm from "../../common/ModalBasicConfirm";
import "./AppPayCompanyConfig.css";

type AppPayCompanyConfigProps = {
  company: Company | null;
  checkCompany: CheckCompany | null;
  federalEIN: string | null;
  onCompanyUpdate: () => void;
};

type CompanyFormState = {
  legalName: string;
  tradeName: string;
  website: string;
  firstPayrollDateCF: string;
  business_type: CheckBusinessType;
  industry_type: CheckIndustryType;
  federal_ein: string;
  address: {
    line1: string;
    line2: string;
    postal_code: string;
    city: string;
    state: string;
    country: string;
  };
};

const formatBusinessTypeDisplay = (value: CheckBusinessType): string => {
  return value
    .split("_")
    .map((word) => {
      // Special case: "llc" should be displayed as "LLC"
      if (word.toLowerCase() === "llc") {
        return "LLC";
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const formatIndustryTypeDisplay = (value: CheckIndustryType): string => {
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const businessTypeOptions: GenericSelectOption[] = CHECK_BUSINESS_TYPES.map((type) => ({
  displayName: formatBusinessTypeDisplay(type),
  value: type,
}));

const industryTypeOptions: GenericSelectOption[] = CHECK_INDUSTRY_TYPES.map((type) => ({
  displayName: formatIndustryTypeDisplay(type),
  value: type,
}));

const parseAddress = (addressString: string): Address => {
  try {
    const parsed = JSON.parse(addressString);
    return {
      line1: parsed.line1 || "",
      line2: parsed.line2 || "",
      postal_code: parsed.postal_code || "",
      city: parsed.city || "",
      state: parsed.state || "",
      country: parsed.country || "",
    };
  } catch {
    return {
      line1: "",
      line2: "",
      postal_code: "",
      city: "",
      state: "",
      country: "",
    };
  }
};

const buildCompanyFormState = (
  company: Company | null,
  checkCompany: CheckCompany | null,
  federalEIN: string | null = null
): CompanyFormState => {
  if (!company) {
    return {
      legalName: "",
      tradeName: "",
      website: "",
      firstPayrollDateCF: "",
      business_type: "c_corporation",
      industry_type: "other",
      federal_ein: "",
      address: {
        line1: "",
        line2: "",
        postal_code: "",
        city: "",
        state: "",
        country: "",
      },
    };
  }

  const address = parseAddress(company.address);

  return {
    legalName: company.legalName || "",
    tradeName: company.tradeName || "",
    website: company.website || "",
    firstPayrollDateCF: company.firstPayrollDateCF || "",
    business_type: checkCompany?.business_type as CheckBusinessType || "c_corporation",
    industry_type: checkCompany?.industry_type as CheckIndustryType || "other",
    federal_ein: federalEIN || "",
    address: {
      line1: address.line1 || "",
      line2: address.line2 || "",
      postal_code: address.postal_code || "",
      city: address.city || "",
      state: address.state || "",
      country: address.country || "",
    },
  };
};

export const AppPayCompanyConfig = ({
  company: propCompany,
  checkCompany: propCheckCompany,
  federalEIN: propFederalEIN,
  onCompanyUpdate,
}: AppPayCompanyConfigProps) => {
  const dispatch = useDispatch();
  const uiStates = useSelector(selectUIStates);

  const [company, setCompany] = useState<Company | null>(propCompany);
  const [checkCompany, setCheckCompany] = useState<CheckCompany | null>(propCheckCompany);
  const [federalEIN, setFederalEIN] = useState<string | null>(propFederalEIN);
  const [saving, setSaving] = useState<boolean>(false);
  const [formState, setFormState] = useState<CompanyFormState>(
    buildCompanyFormState(propCompany, propCheckCompany, propFederalEIN)
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showEINConfirmationModal, setShowEINConfirmationModal] = useState<boolean>(false);
  const [pendingSave, setPendingSave] = useState<(() => void) | null>(null);

  useEffect(() => {
    dispatch(setUIStates({ ...uiStates, hrisLocation: "Payroll Configuration" }));
  }, [dispatch]);

  useEffect(() => {
    setCompany(propCompany);
    setCheckCompany(propCheckCompany);
    setFederalEIN(propFederalEIN);
  }, [propCompany, propCheckCompany, propFederalEIN]);

  useEffect(() => {
    if (company) {
      // Ensure federalEIN is a string before building form state
      setFormState(buildCompanyFormState(company, checkCompany, federalEIN));
    }
  }, [company, checkCompany, federalEIN]);

  const handleFieldChange = (
    field: keyof CompanyFormState,
    value: string
  ) => {
    if (field === "address") {
      return; // Address fields handled separately
    }
    // Ensure federal_ein is always a string
    const safeValue = field === "federal_ein" && typeof value !== "string" 
      ? String(value || "") 
      : value;
    setFormState((prev) => ({
      ...prev,
      [field]: safeValue,
    }));
  };

  const handleAddressFieldChange = (
    field: keyof CompanyFormState["address"],
    value: string
  ) => {
    setFormState((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };


  const performSave = async () => {
    if (!company) {
      toast("No company data available.", toastFail);
      return;
    }

    if (!company.checkCompanyId) {
      toast("Extended company id not found.", toastFail);
      return;
    }

    if (!checkCompany) {
      toast("Extended company data not found.", toastFail);
      return;
    }

    // track if federal ein has changed
    const federalEINChanged = federalEIN && formState.federal_ein.trim() !== federalEIN.trim();

    // Validation
    if (!formState.legalName.trim()) {
      toast("Legal name is required", toastFail);
      return;
    }

    if (!formState.website.trim()) {
      toast("Website is required", toastFail);
      return;
    }

    // Validate website format
    try {
      new URL(formState.website.trim());
    } catch {
      // If URL parsing fails, try adding https://
      try {
        new URL(`https://${formState.website.trim()}`);
      } catch {
        setError("Please enter a valid website URL");
        return;
      }
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    // if federal ein has changed, we need to reverify the federal ein
    if (federalEINChanged) {
      const apiResponse = await apiCheckUpdateFederalEIN(company.checkCompanyId, formState.federal_ein.trim());
      const result = apiResponse?.data;

      if (!apiResponse || isCheckError(apiResponse) || !result) {
        setError("Failed to update federal ein");
        toast("Failed to update federal ein", toastFail);
        setSaving(false);
        return;
      }

      if (!result) {
        setError("Failed to update federal ein");
        toast("Failed to update federal ein", toastFail);
        setSaving(false);
        return;
      }

      toast("Updated federal EIN", toastSuccess);
      setFederalEIN(formState.federal_ein.trim());
    }

    // update the shared pieces
    try {
      // Build address JSON string
      // Preserve original country value since it's read-only
      const originalAddress = parseAddress(company.address);
      const addressObj: Address = {
        line1: formState.address.line1.trim(),
        line2: formState.address.line2.trim() || undefined,
        postal_code: formState.address.postal_code.trim(),
        city: formState.address.city.trim(),
        state: formState.address.state.trim(),
        country: originalAddress.country || undefined,
      };

      const addressString = JSON.stringify(addressObj);

      const updatedCompany = await apiUpdateCompany(company.id, {
        legalName: formState.legalName.trim(),
        tradeName: formState.tradeName.trim(),
        website: formState.website.trim(),
        checkCompanyId: company.checkCompanyId,
        address: addressString,
      });

      if (updatedCompany) {
        setCompany(updatedCompany);
        setSuccess("Company configuration updated successfully");
        toast("Company updated successfully", toastSuccess);
      } else {
        setError("Failed to update company");
        toast("Failed to update company", toastFail);
        setSaving(false);
        return;
      }
    } catch (err) {
      console.error("[AppPayCompanyConfig] Error updating company:", err);
      setError("Failed to update company configuration");
      toast("Error updating company", toastFail);
      setSaving(false);
    } finally {
      setSaving(false);
    }

    // update the check company stuff that we don't store in our db
    // but only if it changed
    const checkCompanyChanged = 
      checkCompany?.business_type !== formState.business_type || 
      checkCompany?.industry_type !== formState.industry_type;

    if (checkCompanyChanged) {
      try {
        const payload : Partial<CheckCompany> = {
          business_type: formState.business_type,
          industry_type: formState.industry_type,
        }

        const apiResponse = await apiCheckUpdateCompany(company.checkCompanyId, payload);
        const updatedCheckCompany = apiResponse?.data;

        if (!apiResponse || isCheckError(apiResponse) || !updatedCheckCompany) {
          setError("Failed to update extended company");
          toast("Failed to update extended company", toastFail);
          setSaving(false);
          return;
        }

        setCheckCompany(updatedCheckCompany);
        setSuccess("Extended company updated successfully");
        toast("Extended company updated successfully", toastSuccess);
        setSaving(false);
        // Reload company data from parent
        onCompanyUpdate();
      } catch (err) {
        console.error("[AppPayCompanyConfig] Error updating check company:", err);
        setError("Failed to update extended company");
        toast("Failed to update extended company", toastFail);
        setSaving(false);
      }
    }

    setSaving(false);
    // Reload company data from parent after save
    onCompanyUpdate();
  };

  const handleSave = async () => {
    // Prevent saving if onboarding is not completed
    if (checkCompany?.onboard?.status !== "completed") {
      toast("Cannot save changes: Company onboarding is not completed", toastFail);
      return;
    }

    if (!company || !federalEIN) {
      // If no EIN exists, proceed without confirmation
      await performSave();
      return;
    }

    // Check if legal name or EIN has changed
    const legalNameChanged = formState.legalName.trim() !== company.legalName.trim();
    const einChanged = formState.federal_ein.trim() !== federalEIN.trim();

    if (legalNameChanged || einChanged) {
      // Show confirmation modal
      setPendingSave(() => performSave);
      setShowEINConfirmationModal(true);
    } else {
      // No changes to legal name or EIN, proceed with save
      await performSave();
    }
  };

  const handleConfirmEINChange = () => {
    setShowEINConfirmationModal(false);
    if (pendingSave) {
      pendingSave();
      setPendingSave(null);
    }
  };

  const handleCancelEINChange = () => {
    setShowEINConfirmationModal(false);
    setPendingSave(null);
  };

  if (!company) {
    return (
      <div className="app-pay-company-config">
        <div className="app-pay-company-config-empty">
          <p>No company data found for this workspace.</p>
        </div>
      </div>
    );
  }

  // console.log("[AppPayCompanyConfig] checkCompany", checkCompany);
  // console.log("[AppPayCompanyConfig] federalEIN", federalEIN);
  // console.log("[AppPayCompanyConfig] formState", formState);

  const onboardingNotCompleted = checkCompany?.onboard?.status !== "completed";
  
  // Validate all required fields
  const isFormValid = (): boolean => {
    // Required fields
    if (!formState.legalName.trim()) return false;
    if (!formState.website.trim()) return false;
    if (!isValidURL(formState.website.trim())) return false;
    if (!formState.business_type) return false;
    if (!formState.industry_type) return false;
    
    // Address required fields
    if (!formState.address.line1.trim()) return false;
    if (!formState.address.city.trim()) return false;
    if (!formState.address.state.trim()) return false;
    if (!formState.address.postal_code.trim()) return false;
    if (!isValidPostalCode(formState.address.postal_code.trim())) return false;
    
    // Federal EIN validation (if present, must be valid)
    if (formState.federal_ein && formState.federal_ein.trim().length > 0) {
      if (!isValidFederalEIN(formState.federal_ein.trim())) return false;
    }
    
    return true;
  };
  
  return (
    <div className="app-pay-company-config">
      {error && (
        <Alert variant="destructive" className="app-pay-company-config-alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="app-pay-company-config-alert success-alert">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <div className="app-pay-company-config-form">
            <div className="two-column-grid">
              <div className="column-content">
                <div className="field-group">
                  <Label htmlFor="legalName">Legal Name *</Label>
                  <Input
                    id="legalName"
                    value={formState.legalName}
                    onChange={(e) =>
                      handleFieldChange("legalName", e.target.value)
                    }
                    disabled={saving}
                  />
                </div>

                <div className="field-group">
                  <Label htmlFor="tradeName">Trade Name (DBA)</Label>
                  <Input
                    id="tradeName"
                    value={formState.tradeName}
                    onChange={(e) =>
                      handleFieldChange("tradeName", e.target.value)
                    }
                    disabled={saving}
                  />
                </div>

                <div className="field-group">
                  <Label 
                    htmlFor="website"
                    required
                    invalid={!!formState.website && formState.website.trim().length > 0 && !isValidURL(formState.website.trim())}
                    invalidReason="Please enter a valid website URL"
                  >
                    Website
                  </Label>
                  <Input
                    id="website"
                    type="url"
                    value={formState.website}
                    onChange={(e) =>
                      handleFieldChange("website", e.target.value)
                    }
                    disabled={saving}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="field-group">
                  <Label htmlFor="business_type">Business Type</Label>
                  <GenericSelect
                    options={businessTypeOptions}
                    selected={businessTypeOptions.find(
                      (opt) => opt.value === formState.business_type
                    )}
                    onChange={(value: any) => {
                      setFormState((prev) => ({
                        ...prev,
                        business_type: value as CheckBusinessType,
                      }));
                    }}
                    disabled={saving}
                    maxHeight="200px"
                  />
                </div>

                <div className="field-group">
                  <Label htmlFor="industry_type">Industry Type</Label>
                  <GenericSelect
                    options={industryTypeOptions}
                    selected={industryTypeOptions.find(
                      (opt) => opt.value === formState.industry_type
                    )}
                    onChange={(value: any) => {
                      setFormState((prev) => ({
                        ...prev,
                        industry_type: value as CheckIndustryType,
                      }));
                    }}
                    disabled={saving}
                    maxHeight="200px"
                  />
                </div>

                <div className="field-group">
                  <Label htmlFor="firstPayrollDateCF">First Payroll Date</Label>
                  <div className="field-value disabled">
                    {company.firstPayrollDateCF
                      ? new Date(company.firstPayrollDateCF).toLocaleDateString()
                      : "—"}
                  </div>
                  <div className="field-hint">
                    This field cannot be edited after company creation
                  </div>
                </div>

                <div className="field-group">
                  <Label 
                    htmlFor="federal_ein"
                    invalid={!!formState.federal_ein && formState.federal_ein.trim().length > 0 && !isValidFederalEIN(formState.federal_ein.trim())}
                    invalidReason="Please enter a valid Federal EIN (##-#######)"
                  >
                    Federal EIN
                  </Label>
                  {federalEIN ? (
                    <div className="ein-input-wrapper">
                      <Input
                        id="federal_ein"
                        placeholder="##-#######"
                        value={typeof formState.federal_ein === "string" ? formState.federal_ein : ""}
                        maxLength={10}
                        onChange={(e) => {
                          const formatted = formatFederalEIN(e.target.value);
                          handleFieldChange("federal_ein", formatted);
                        }}
                        disabled={saving}
                      />
                      {!federalEIN && (
                        <TooltipProvider>
                          <Tooltip>
                            <span className="ein-warning-icon">
                              <AlertTriangle size={16} />
                            </span>
                            <div className="ein-tooltip-content">
                              No federal EIN found.
                            </div>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  ) : (
                    <div className="ein-no-ein-pill">No EIN</div>
                  )}
                </div>
              </div>

              <div className="column-content">
                <div className="field-group">
                  <Label htmlFor="address_line1">Address Line 1</Label>
                  <Input
                    id="address_line1"
                    value={formState.address.line1}
                    onChange={(e) =>
                      handleAddressFieldChange("line1", e.target.value)
                    }
                    disabled={saving}
                  />
                </div>

                <div className="field-group">
                  <Label htmlFor="address_line2">Address Line 2</Label>
                  <Input
                    id="address_line2"
                    value={formState.address.line2}
                    onChange={(e) =>
                      handleAddressFieldChange("line2", e.target.value)
                    }
                    disabled={saving}
                  />
                </div>

                <div className="field-group">
                  <Label htmlFor="address_city">City</Label>
                  <Input
                    id="address_city"
                    value={formState.address.city}
                    onChange={(e) =>
                      handleAddressFieldChange("city", e.target.value)
                    }
                    disabled={saving}
                  />
                </div>

                <div className="field-group">
                  <Label htmlFor="address_state">State</Label>
                  <Input
                    id="address_state"
                    value={formState.address.state}
                    onChange={(e) =>
                      handleAddressFieldChange("state", e.target.value)
                    }
                    disabled={saving}
                  />
                </div>

                <div className="field-group">
                  <Label 
                    htmlFor="address_postal_code"
                    invalid={!!formState.address.postal_code && formState.address.postal_code.trim().length > 0 && !isValidPostalCode(formState.address.postal_code)}
                    invalidReason="Please enter a valid postal code (##### or #####-####)"
                  >
                    Postal Code
                  </Label>
                  <Input
                    id="address_postal_code"
                    placeholder="#####-####"
                    value={formState.address.postal_code}
                    maxLength={10}
                    onChange={(e) => {
                      const formatted = formatPostalCode(e.target.value);
                      handleAddressFieldChange("postal_code", formatted);
                    }}
                    disabled={saving}
                  />
                </div>

                <div className="field-group">
                  <Label htmlFor="address_country">Country</Label>
                  <div className="field-value disabled">
                    {formState.address.country || "—"}
                  </div>
                  <div className="field-hint">
                    This field cannot be edited
                  </div>
                </div>
              </div>
            </div>

            <div className="app-pay-company-config-form-actions">
              <Button
                label="Save Changes"
                variant="primary"
                leftIcon={<Save size={16} />}
                onClick={handleSave}
                disabled={saving || onboardingNotCompleted || !isFormValid()}
              />
            </div>
          </div>

      <ModalBasicConfirm
        isOpen={showEINConfirmationModal}
        onClose={handleCancelEINChange}
        title="Federal EIN Verification Required"
        message="Editing the legal company name or Federal EIN will require reverifying the Federal EIN, which may take a while. Do you want to continue?"
        onContinue={handleConfirmEINChange}
        continueLabel="Continue"
        allowClickout={false}
        allowDismiss={true}
      />
    </div>
  );
};

